// Lightweight global cart manager to avoid duplicate network requests across instances
if (!window.B2BCartSyncManager) {
    window.B2BCartSyncManager = (function() {
        let cachedCart = null;
        let isFetching = false;
        let pendingResolvers = [];
        const subscribers = new Map(); // variantId -> Set<callback>

        async function fetchCartInternal() {
            try {
                // Always fetch fresh cart from Shopify to avoid stale/lazy lag
                const response = await fetch('/cart.js', { cache: 'no-store' });
                return await response.json();
            } catch (e) {
                console.error('[B2BCartSyncManager] fetch error', e);
                return null;
            }
        }

        async function refresh() {
            console.log('[B2BCartSyncManager] refresh called, isFetching:', isFetching);
            if (isFetching) {
                console.log('[B2BCartSyncManager] already fetching, queuing resolver');
                return new Promise(resolve => pendingResolvers.push(resolve));
            }
            isFetching = true;
            console.log('[B2BCartSyncManager] fetching cart...');
            const cart = await fetchCartInternal();
            console.log('[B2BCartSyncManager] fetched cart:', cart);
            cachedCart = cart;
            isFetching = false;
            const resolvers = pendingResolvers.splice(0, pendingResolvers.length);
            resolvers.forEach(r => r(cart));
            if (cart && cart.items) {
                console.log('[B2BCartSyncManager] notifying subscribers...');
                notify(cart);
            } else {
                console.log('[B2BCartSyncManager] no cart or items, skipping notify');
            }
            return cart;
        }

        function getItemByVariant(cart, variantId) {
            if (!cart || !cart.items) return null;
            return cart.items.find(i => (
                i?.variantId == variantId ||
                i?.variant_id == variantId ||
                i?.id == variantId
            ));
        }

        function notify(cart) {
            if (!cart || !cart.items) return;
            console.log('[B2BCartSyncManager] notify cart:', cart);
            console.log('[B2BCartSyncManager] subscribers count:', subscribers.size);
            subscribers.forEach((callbacks, variantId) => {
                const item = getItemByVariant(cart, variantId);
                const qty = item ? item.quantity : 0;
                console.log(`[B2BCartSyncManager] variantId: ${variantId}, item:`, item, 'qty:', qty, 'item found:', !!item);
                callbacks.forEach(cb => {
                    try { 
                        console.log(`[B2BCartSyncManager] calling callback for variantId: ${variantId} with qty: ${qty}`);
                        cb(qty, cart); 
                    } catch (e) { 
                        console.error('[B2BCartSyncManager] subscriber error', e); 
                    }
                });
            });
            // Fallback: ensure any (re)rendered selectors also get updated even if not subscribed yet
            try {
                requestAnimationFrame(() => {
                    document.querySelectorAll('b2b-quantity-selector').forEach((el) => {
                        const variantId = el?.dataset?.variantId;
                        if (!variantId) return;
                        const item = getItemByVariant(cart, variantId);
                        const qty = item ? item.quantity : 0;
                        const inputEl = el.querySelector?.('.qty-input-plp');
                        if (!inputEl) return;
                        const currentValue = parseInt(inputEl.value) || 0;
                        if (currentValue !== qty) {
                            // Guard against triggering change handlers
                            el._isSyncingFromManager = true;
                            inputEl.value = qty;
                            if (qty > 0) {
                                inputEl.dispatchEvent(new Event('input', { bubbles: true }));
                            }
                            requestAnimationFrame(() => { el._isSyncingFromManager = false; });
                        }
                    });
                });
            } catch (e) {
                console.error('[B2BCartSyncManager] dom sync fallback error', e);
            }
        }

        function subscribe(variantId, callback) {
            console.log('[B2BCartSyncManager] subscribe called for variantId:', variantId);
            if (!subscribers.has(variantId)) subscribers.set(variantId, new Set());
            subscribers.get(variantId).add(callback);
            console.log('[B2BCartSyncManager] total subscribers now:', subscribers.size);
            // Immediate push with cached cart if available
            if (cachedCart && cachedCart.items) {
                const item = getItemByVariant(cachedCart, variantId);
                const qty = item ? item.quantity : 0;
                console.log('[B2BCartSyncManager] immediate callback for variantId:', variantId, 'qty:', qty);
                try { callback(qty, cachedCart); } catch (e) { console.error('[B2BCartSyncManager] immediate callback error:', e); }
            } else {
                console.log('[B2BCartSyncManager] no cached cart, triggering refresh');
                // ensure at least one fetch happens
                refresh();
            }
            return () => unsubscribe(variantId, callback);
        }

        function unsubscribe(variantId, callback) {
            const set = subscribers.get(variantId);
            if (!set) return;
            set.delete(callback);
            if (set.size === 0) subscribers.delete(variantId);
        }

        function initNetworkObserversOnce() {
            try {
                if (!window.__b2bOriginalFetch) {
                    window.__b2bOriginalFetch = window.fetch.bind(window);
                    const isCartEndpoint = (url) => {
                        try {
                            const u = typeof url === 'string' ? new URL(url, window.location.origin) : new URL(url.url || url, window.location.origin);
                            return /\/cart\/(add|change|update)\.js$/.test(u.pathname);
                        } catch (_) {
                            return false;
                        }
                    };
                    window.fetch = async (...args) => {
                        const matches = isCartEndpoint(args[0]);
                        const response = await window.__b2bOriginalFetch(...args);
                        if (matches) {
                            Promise.resolve().then(() => refresh());
                        }
                        return response;
                    };
                }
                if (!window.__b2bXHROverridden && window.XMLHttpRequest) {
                    window.__b2bXHROverridden = true;
                    const OriginalXHR = window.XMLHttpRequest;
                    const isCartEndpointPath = (path) => /\/cart\/(add|change|update)\.js$/.test(path || '');
                    function XHRWrapper() {
                        const xhr = new OriginalXHR();
                        let requestUrl = '';
                        const originalOpen = xhr.open;
                        xhr.open = function(method, url, ...rest) {
                            try {
                                const u = new URL(url, window.location.origin);
                                requestUrl = u.pathname;
                            } catch (_) {
                                requestUrl = typeof url === 'string' ? url : '';
                            }
                            return originalOpen.call(this, method, url, ...rest);
                        };
                        xhr.addEventListener('loadend', function() {
                            if (isCartEndpointPath(requestUrl)) {
                                Promise.resolve().then(() => refresh());
                            }
                        });
                        return xhr;
                    }
                    window.XMLHttpRequest = XHRWrapper;
                }
            } catch (e) {
                console.error('[B2BCartSyncManager] network observer init failed', e);
            }
        }

        function optimisticUpdate(variantId, quantity) {
            // Update cached cart immediately to avoid visible lag
            const nextQty = Number.isFinite(quantity) ? quantity : 0;
            console.log(`[B2BCartSyncManager] optimisticUpdate variantId: ${variantId}, quantity: ${quantity}, nextQty: ${nextQty}`);
            if (!cachedCart || !cachedCart.items) {
                // synth notify without cart
                console.log(`[B2BCartSyncManager] no cached cart, notifying with qty: ${nextQty}`);
                subscribers.get(variantId)?.forEach(cb => { try { cb(nextQty, null); } catch(e){} });
                return;
            }
            const item = getItemByVariant(cachedCart, variantId);
            console.log(`[B2BCartSyncManager] found item:`, item);
            if (item) {
                if (nextQty <= 0) {
                    // Remove item from cart when quantity is 0 or negative
                    console.log(`[B2BCartSyncManager] removing item from cart`);
                    const index = cachedCart.items.indexOf(item);
                    if (index > -1) {
                        cachedCart.items.splice(index, 1);
                        console.log(`[B2BCartSyncManager] item removed, new items count: ${cachedCart.items.length}`);
                    }
                } else {
                    item.quantity = nextQty;
                    console.log(`[B2BCartSyncManager] updated item quantity to: ${nextQty}`);
                }
            } else if (nextQty > 0) {
                // Create a minimal item so subscribers get the value; leave other fields empty
                console.log(`[B2BCartSyncManager] creating new item with qty: ${nextQty}`);
                cachedCart.items.push({ id: variantId, variant_id: variantId, variantId, quantity: nextQty });
            }
            // Always notify, even when removing items
            console.log(`[B2BCartSyncManager] notifying after optimistic update`);
            notify(cachedCart);
        }

        function setCart(cart) {
            if (!cart || !cart.items) return;
            cachedCart = cart;
            notify(cart);
        }

        function init() {
            initNetworkObserversOnce();
            document.addEventListener('cart:change', (e) => {
                const evtCart = e?.detail?.cart;
                if (evtCart && evtCart.items) {
                    console.log('[B2BCartSyncManager] setCart from cart:change event');
                    setCart(evtCart);
                } else {
                    refresh();
                }
            });
            window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
            // Prime cache
            refresh();
        }

        // initialize immediately
        init();

        return { subscribe, unsubscribe, refresh, getCart: () => cachedCart, optimisticUpdate, setCart };
    })();
}

class B2BQuantitySelector extends HTMLElement {
    constructor() {
        super();
        this._abortController = new AbortController();
        this._isSyncingFromManager = false;
        this.inputElement = this.querySelector('.qty-input-plp');
        this.variantId = this.dataset.variantId;
        this.minQty = parseInt(this.inputElement.getAttribute('min')) || 0;
        this.maxQty = parseInt(this.inputElement.getAttribute('max')) || Number.MAX_SAFE_INTEGER;
        this.step = parseInt(this.inputElement.getAttribute('step')) || 1;
    }

    connectedCallback() {
        this.setupEventListeners();
        console.log('[B2BQuantitySelector] connecting variantId:', this.variantId, 'inputElement:', this.inputElement);
        // Subscribe to global cart updates for this variant
        this._unsubscribe = window.B2BCartSyncManager.subscribe(this.variantId, (qty) => {
            console.log('[B2BQuantitySelector] callback triggered for variantId:', this.variantId, 'qty:', qty);
            const currentValue = parseInt(this.inputElement.value) || 0;
            console.log('[B2BQuantitySelector] currentValue:', currentValue, 'newValue:', qty);
            if (currentValue !== qty) {
                console.log('[B2BQuantitySelector] updating input from', currentValue, 'to', qty);
                this._isSyncingFromManager = true;
                this.inputElement.value = qty;
                // For zero, avoid dispatching input to prevent external min-clamping logic
                if (qty > 0) {
                    // UI update for masks/formatters without triggering business logic
                    this.inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                }
                // release the guard on next frame
                requestAnimationFrame(() => { this._isSyncingFromManager = false; });
            } else {
                console.log('[B2BQuantitySelector] no update needed, values are the same');
            }
        });
    }

    disconnectedCallback() {
        this._abortController.abort();
        if (typeof this._unsubscribe === 'function') this._unsubscribe();
    }

    setupEventListeners() {
        this.querySelectorAll('.qty-btn-plp').forEach(btn => {
            btn.addEventListener('click', () => {
                const currentQty = parseInt(this.inputElement.value) || 0;
                let newQty;

                if (btn.dataset.action === 'increase') {
                    newQty = currentQty + this.step;
                } else {
                    if (currentQty <= this.minQty) {
                        newQty = 0;
                    } else {
                        newQty = Math.max(currentQty - this.step, this.minQty);
                    }
                }

                this.updateQuantity(newQty);
            }, {
                signal: this._abortController.signal
            });
        });

        this.inputElement.addEventListener('change', () => {
            if (this._isSyncingFromManager) {
                // Ignore synthetic change coming from manager sync
                return;
            }
            const newQty = this.validateQuantity(this.inputElement.value);
            this.inputElement.value = newQty;
            this.updateCart(newQty);
        }, {
            signal: this._abortController.signal
        });

        this.inputElement.addEventListener('input', () => {
            const newQty = this.validateQuantity(this.inputElement.value);
            if (parseInt(this.inputElement.value) !== newQty) {
                this.inputElement.value = newQty;
            }
        }, {
            signal: this._abortController.signal
        });
    }

    validateQuantity(qty) {
        qty = parseInt(qty) || 0;
        qty = Math.max(qty, 0);
        qty = Math.min(qty, this.maxQty);

        if (qty === 0) return 0;

        if (qty > 0) {
            qty = Math.max(qty, this.minQty);
            const steps = Math.round((qty - this.minQty) / this.step);
            qty = this.minQty + (steps * this.step);
        }

        return qty;
    }

    updateQuantity(newQty) {
        const validatedQty = this.validateQuantity(newQty);
        this.inputElement.value = validatedQty;
        this.updateCart(validatedQty);
    }

    async updateCart(qty) {
        if (qty < 0) {
            console.warn('Invalid quantity:', qty);
            return;
        }

        this.showLoading();

        try {
            // Optimistically update UI immediately
            window.B2BCartSyncManager.optimisticUpdate(this.variantId, qty);
            const cart = await fetch('/cart.js', { cache: 'no-store' }).then(res => res.json());
            const item = cart.items.find(i => (i?.variantId == this.variantId || i?.variant_id == this.variantId || i?.id == this.variantId));

            if (item) {
                await this.changeLineItem(item.key, qty);
            } else if (qty > 0) {
                await this.addVariant(this.variantId, qty);
            }
            // Ensure a refresh after mutation in case external observers miss it
            window.B2BCartSyncManager.refresh();
        } catch (error) {
            console.error('Cart update error:', error);
            this.showError('Failed to update cart. Please try again.');
            window.B2BCartSyncManager.refresh();
        } finally {
            this.hideLoading();
        }
    }

    async changeLineItem(lineKey, qty) {
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: lineKey,
                quantity: qty
            })
        });

        if (!response.ok) {
            throw new Error(`Cart change failed: ${response.status}`);
        }

        const cartContent = await response.json();
        const fullCart = await fetch('/cart.js').then(res => res.json());
        fullCart.sections = cartContent.sections || {};

        if(window.upcartRefreshCart){
            window.upcartRefreshCart();
            window.upcartSettings.upcartSettings.openOnAddToCart && window.upcartOpenCart()
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
                baseEvent: "line-item:change",
                cart: fullCart
            }
        }));
    }

    async addVariant(variantId, qty) {
        const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                id: variantId,
                quantity: qty
            })
        });

        if (!response.ok) {
            throw new Error(`Cart add failed: ${response.status}`);
        }

        const fullCart = await fetch('/cart.js').then(res => res.json());
        
        if(window.upcartRefreshCart){
            window.upcartRefreshCart();
            window.upcartSettings.upcartSettings.openOnAddToCart && window.upcartOpenCart()
        }

        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
                baseEvent: "variant:add",
                cart: fullCart
            }
        }));
    }

    showLoading() {
        this.classList.add('updating');
    }

    hideLoading() {
        this.classList.remove('updating');
    }

    showError(message) {
        const existingError = this.querySelector('.qty-error-message');
        if (existingError) {
            existingError.remove();
        }

        const errorEl = document.createElement('div');
        errorEl.className = 'qty-error-message';
        errorEl.textContent = message;
        errorEl.style.cssText = `
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: #ff4444;
            color: white;
            padding: 4px 8px;
            font-size: 12px;
            border-radius: 4px;
            z-index: 1000;
            margin-top: 2px;
        `;

        this.style.position = 'relative';
        this.appendChild(errorEl);

        setTimeout(() => {
            if (errorEl.parentNode) {
                errorEl.remove();
            }
        }, 3000);
    }
}

if (!window.customElements.get('b2b-quantity-selector')) {
    window.customElements.define('b2b-quantity-selector', B2BQuantitySelector);
}
